/************************************************************************************

* UTILITIES FUNCTIONALITIES

*************************************************************************************/
// =====================
// DOM Manipulation Utils
// =====================

function l_setInnerHTML(element, html) {
	element.innerHTML = html
	element.querySelectorAll('script').forEach((oldScript) => {
		const newScript = document.createElement('script')
		const attributes = [...oldScript.attributes]

		attributes.forEach((attr) => {
			newScript.setAttribute(attr.name, attr.value)
		})

		newScript.appendChild(document.createTextNode(oldScript.innerHTML))
		oldScript.parentNode.replaceChild(newScript, oldScript)
	})
}

function l_swapHtmlNode(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
	// Run pre-process callbacks on new content
	if (Array.isArray(preProcessCallbacks)) {
		preProcessCallbacks.forEach((cb) => cb?.(newContent))
	}

	// Create a new node from newContent's outerHTML (ensures scripts run)
	const wrapper = document.createElement('div')
	l_setInnerHTML(wrapper, newContent.outerHTML)
	const newNode = wrapper.firstElementChild

	// Deduplicate [id] and [form] in oldNode's subtree
	const timestampKey = Date.now()
	oldNode.querySelectorAll('[id], [form]').forEach((el) => {
		if (el.id) el.id = `${el.id}-${timestampKey}`
		if (el.form) {
			const formId = el.getAttribute('form')
			if (formId) el.setAttribute('form', `${formId}-${timestampKey}`)
		}
	})

	// Insert newNode before oldNode and hide oldNode
	oldNode.parentNode.insertBefore(newNode, oldNode)
	oldNode.style.display = 'none'

	// Run post-process callbacks
	if (Array.isArray(postProcessCallbacks)) {
		postProcessCallbacks.forEach((cb) => cb?.(newNode))
	}

	// Remove oldNode after transition
	setTimeout(() => {
		if (oldNode?.parentNode) oldNode.remove()
	}, 500)
}

function l_getSectionDOM(html, selector = '.shopify-section') {
	return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
}

// =====================
// Scroll & Body Lock Utilities
// =====================

function l_lockBodyScroll(elementName) {
	document.documentElement.classList.add('js-block-page')

	if (elementName) {
		document.documentElement.classList.add(`js-block-page-${elementName}`)
	}
}

function l_unLockBodyScroll(elementName) {
	if (elementName) {
		document.documentElement.classList.remove(`js-block-page-${elementName}`)

		const isExistAnotherBlockedClass = Array.from(document.documentElement.classList).some((cls) =>
			cls.startsWith('js-block-page-')
		)

		!isExistAnotherBlockedClass && document.documentElement.classList.remove('js-block-page')
	} else {
		document.documentElement.classList.remove('js-block-page')
	}
}

// =====================
// Focus Management & Trap Focus
// =====================

function l_getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	).filter((element) => !element.closest('[aria-hidden="true"]'))
}
function l_disableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)
	focusableElements.forEach((element) => {
		element.setAttribute('tabindex', '-1')
		element.setAttribute('aria-disabled', 'true')
	})
}
function l_enableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)

	focusableElements.forEach((element) => {
		element.removeAttribute('tabindex')
		element.removeAttribute('aria-disabled')
	})
}

const l_trapFocusHandlers = {}

function l_trapFocus({
	container,
	defaultElementToFocus,
	enableFocusRange = true,
	firstFocusableElements = [],
	lastFocusableElements = [],
}) {
	const elements = Array.from(
		new Set([...firstFocusableElements, ...l_getFocusableElements(container), ...lastFocusableElements])
	)
	const first = elements[0]
	const last = elements[elements.length - 1]
	let elementToFocus = defaultElementToFocus || first

	l_removeTrapFocus()

	l_trapFocusHandlers.focusin = (event) => {
		if (!container.contains(event.target)) return

		document.addEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	l_trapFocusHandlers.focusout = function () {
		document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	l_trapFocusHandlers.keydown = function (event) {
		if (event.code.toUpperCase() !== 'TAB') return // If not TAB key

		// On the last focusable element and tab forward, focus the first element.
		if ((event.target === last || last.contains(event.target)) && !event.shiftKey) {
			event.preventDefault()
			first.focus()
		}

		//  On the first focusable element and tab backward, focus the last element.
		if ((event.target === container || first.contains(event.target)) && event.shiftKey) {
			event.preventDefault()
			last.focus()
		}
	}

	document.addEventListener('focusout', l_trapFocusHandlers.focusout)
	document.addEventListener('focusin', l_trapFocusHandlers.focusin)

	if (elementToFocus) {
		elementToFocus.focus()

		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value &&
			enableFocusRange
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length)
		}
	}
}
function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', l_trapFocusHandlers.focusin)
	document.removeEventListener('focusout', l_trapFocusHandlers.focusout)
	document.removeEventListener('keydown', l_trapFocusHandlers.keydown)

	if (elementToFocus) elementToFocus.focus()
}

function l_setupGlobalVariables() {
	// Set scrollbar width
	const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
	document.documentElement.style.setProperty('--l-body-scrollbar-width', `${scrollBarWidth}px`)
}

// =====================
// Variables Setup
// =====================

function l_setupVariablesBeforeOpenModal() {
	window.LTAAnnouncementBarSectionEl =
		window.LTAAnnouncementBarSectionEl || document.querySelector('.l-announcement-bar')
	window.LTAHeaderSectionEl = window.LTAHeaderSectionEl || document.querySelector('.l-header')

	document.documentElement.style.setProperty('--l-viewport-height', `${visualViewport?.height ?? window.innerHeight}px`)
	document.documentElement.style.setProperty('--l-header-height', `${window.LTAHeaderSectionEl?.offsetHeight || 0}px`)
	document.documentElement.style.setProperty(
		'--l-header-bottom-position',
		`${window.LTAHeaderSectionEl.getBoundingClientRect().bottom}px`
	)
	document.documentElement.style.setProperty(
		'--l-announcement-bar-height',
		`${window.LTAAnnouncementBarSectionEl?.offsetHeight || 0}px`
	)
}
function l_isReturningVisitorAfterSession() {
	const firstVisitId = localStorage.getItem('hasVisited')
	let currentSessionId = sessionStorage.getItem('sessionStarted')

	if (!currentSessionId) {
		currentSessionId = Date.now()
		sessionStorage.setItem('sessionStarted', currentSessionId)
	}

	if (!firstVisitId) {
		localStorage.setItem('hasVisited', currentSessionId)
	}

	if (firstVisitId && currentSessionId && firstVisitId != currentSessionId) {
		window.isReturningCustomer = true

		document.dispatchEvent(
			new CustomEvent('l:hasVisitedBefore', { bubbles: true, detail: { isReturningCustomer: true } })
		)

		return
	}

	// Either a first-time visitor or still in same session
	window.isReturningCustomer = false
	document.dispatchEvent(
		new CustomEvent('l:hasVisitedBefore', { bubbles: true, detail: { isReturningCustomer: false } })
	)

	return false
}

// =====================
// Media Controls
// =====================

function l_pauseAllMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.pauseVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"pause"}', '*')
	})
	container.querySelectorAll('video').forEach((video) => video.pause())
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.pause()
	})
	container.querySelectorAll('l-media-video').forEach((video) => {
		video.player.pause()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.pause()
	})
}

function l_playMedia(container = document) {
	container.querySelectorAll('l-external-media').forEach((video) => {
		video?.playVideo?.()
	})
	container.querySelectorAll('.js-youtube').forEach((video) => {
		video.contentWindow.postMessage('{"event":"command","func":"' + 'playVideo' + '","args":""}', '*')
	})
	container.querySelectorAll('.js-vimeo').forEach((video) => {
		video.contentWindow.postMessage('{"method":"play"}', '*')
	})
	container.querySelectorAll('video').forEach((video) => video.play())
	container.querySelectorAll('product-model').forEach((model) => {
		if (model.modelViewerUI) model.modelViewerUI.play()
	})
	container.querySelectorAll('l-media-video').forEach((video) => {
		video.player.play()
	})
	container.querySelectorAll('video').forEach((video) => {
		video.play()
	})
}

// =====================
// Money Formatting
// =====================

function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.T4Sconfigs.moneyWithCurrencyFormat || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', '.')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function l_handleize(str) {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/^-+|-+$/g, '')
		.replace(/--+/g, '-')
}

// =====================
// Event/Utility Functions
// =====================

function l_debounce(fn, wait) {
	let t

	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function l_smoothScrollOnAnchorTags() {
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		const id = anchor.getAttribute('href').split('#')?.[1]
		if (!id) return

		anchor.addEventListener('click', (e) => {
			e.preventDefault()

			const target = document.querySelector(anchor.getAttribute('href'))
			target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
		})
	})
}

function l_intelligenceEvents(eventName) {
	// console.clear()
	console.log('🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨')
	console.log(eventName)
	console.log('🚨🚨🚨🚨🚨🚨🚨🚨🚨🚨')

	window.igEvents = window.igEvents || []
	window.igEvents.push({ event: eventName })
}

/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
	constructor(cartDrawer = null) {
		this.cartDrawer = cartDrawer
	}

	getBaseConfig() {
		return {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
	}

	createConfigFromForm(formEl) {
		const config = this.getBaseConfig()
		const formData = new FormData(formEl)

		config.body = formData

		return config
	}

	createConfigFromItems(items = []) {
		const config = this.getBaseConfig()
		const formData = new FormData()

		items.forEach((item, index) => {
			const prefix = `items[${index}]`

			formData.append(`${prefix}[id]`, item.id)
			formData.append(`${prefix}[quantity]`, item.quantity)

			// Add all other properties from the item object except id, quantity, and selling_plan
			Object.entries(item).forEach(([key, value]) => {
				if (['id', 'quantity'].includes(key)) return
				// If the value is an object, serialize it as JSON
				if (typeof value === 'object' && value !== null) {
					formData.append(`${prefix}[${key}]`, JSON.stringify(value))
				} else if (value !== undefined) {
					formData.append(`${prefix}[${key}]`, value)
				}
			})
		})

		config.body = formData
		return config
	}

	async addToCart(config, id = 'default') {
		// Note: Don't use finally / catch here

		return fetch(`${window.LTATheme.routes.cart_add_url}.js`, config)
			.then((res) => res.json())
			.then((data) => {
				if (data?.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

					return data
				}

				document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}

	async changeCartLineItem(formData) {
		return fetch(`${window.LTATheme.routes.cart_change_url}.js`, { ...l_fetchConfig(), body: JSON.stringify(formData) })
			.then((res) => res.json())
			.then((data) => {
				return data
			})
	}
}

const ltaCartService = new LTACartServices()

/**************************************
 * [FUNCTION]: RUN GLOBAL FUNCTIONS
 ***************************************/
document.addEventListener('DOMContentLoaded', function () {
	l_isReturningVisitorAfterSession()
	l_setupGlobalVariables()
	l_setupVariablesBeforeOpenModal() /* Set --l-header-height on load to prevent layout jump */
	l_smoothScrollOnAnchorTags()
})

window.addEventListener(
	'resize',
	l_debounce(() => {
		l_setupVariablesBeforeOpenModal()
	}, 20)
)

window.addEventListener(
	'scroll',
	l_debounce(() => {
		l_setupVariablesBeforeOpenModal()
	}, 20)
)

/**************************************
 * [FUNCTION]: DETECT INPUT METHOD (MOUSE OR KEYBOARD)
 ***************************************/
document.addEventListener('DOMContentLoaded', function () {
	const events = ['mousemove', 'mousedown']
	events.forEach((eventName) => {
		document.addEventListener(
			eventName,
			l_debounce(() => {
				window.isUsingMouse = true
				document.documentElement.classList.add('is-using-mouse')
				document.documentElement.classList.remove('is-using-keyboard')
			}, 30)
		)
	})

	document.addEventListener('keydown', (e) => {
		if (e.key === 'Tab') {
			window.isUsingMouse = false
			document.documentElement.classList.add('is-using-keyboard')
			document.documentElement.classList.remove('is-using-mouse')
		}
	})
})

/************************************************************************************

* GLOBAL COMPONENTS

*************************************************************************************/

/**************************************
 * [COMPONENT]: L-DRAWER
 **************************************/
class LTADrawer extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		this.setupDrawer()
	}

	setupDrawer() {
		// Elements
		this.triggerEls = document.querySelectorAll(this.dataset.triggerSelector)
		this.closeBtnEls = this.querySelectorAll('[data-close]')
		this.drawerInnerEl = this.querySelector('[data-drawer-inner]')
		this.triggerEl = null

		// Data
		this.onlyOneDrawerOpen = this.dataset.onlyOneDrawerOpen === 'true'
		this.drawerName = this.dataset.drawerName || 'drawer'

		// Setup events

		// Close this drawer if another drawer is open
		if (this.onlyOneDrawerOpen) {
			document.addEventListener('l:drawer:open', (event) => {
				if (event.detail.drawerName !== this.drawerName) {
					this.drawer_close()
				}
			})
		}

		// Open / close (toggle) event
		this.triggerEls.forEach((triggerEl) => {
			triggerEl.addEventListener('click', (event) => {
				event.preventDefault()

				if (this.classList.contains('is-active')) {
					this.drawer_close()
				} else {
					this.drawer_open(triggerEl)
				}
			})
		})

		// Close event
		this.closeBtnEls.forEach((closeBtnEl) => {
			closeBtnEl.addEventListener('click', () => {
				this.drawer_close()
			})
		})
	}

	drawer_open(triggerEl) {
		this.setupAOS('remove')
		l_setupVariablesBeforeOpenModal()
		l_lockBodyScroll(this.drawerName)

		// Open drawer
		this.classList.remove('l-hidden')

		requestAnimationFrame(() => {
			this.classList.add('is-active')
		})

		// Setup focus trap on main content
		if (triggerEl) {
			this.triggerEl = triggerEl
		}

		l_trapFocus({ container: this.drawerInnerEl })
		this.setupAOS('add')

		// Set global events
		window.addEventListener('click', this.drawer_closeOutsideOnClick.bind(this))
		window.addEventListener('keyup', this.drawer_closeOutsideOnEscape.bind(this))

		// Dispatch event to notify that the drawer is open
		document.dispatchEvent(
			new CustomEvent('l:drawer:open', {
				detail: { drawerName: this.drawerName },
			})
		)
	}

	drawer_close() {
		l_unLockBodyScroll(this.drawerName)

		// Close drawer
		this.classList.remove('is-active')

		window.l_debounce(() => {
			this.classList.add('l-hidden')
		}, 400)()

		// Remove focus trap
		if (this.triggerEl) {
			l_removeTrapFocus(this.triggerEl)
			this.triggerEl = null
		}

		// Remove global events
		window.removeEventListener('click', this.drawer_closeOutsideOnClick)
		window.removeEventListener('keyup', this.drawer_closeOutsideOnEscape)
	}

	drawer_closeOutsideOnClick(event) {
		const target = event.target
		const isDrawerTrigger = event.target.closest('l-drawer-trigger') || event.target.tagName == 'l-drawer-trigger'

		const isOutsideClick =
			!this.drawerInnerEl.contains(target) &&
			!Array.from(this.triggerEls).some((el) => el.contains(target)) &&
			!isDrawerTrigger

		if (isOutsideClick) this.drawer_close()
	}

	drawer_closeOutsideOnEscape(event) {
		if (event.code.toUpperCase() === 'ESCAPE') {
			this.drawer_close()
		}
	}

	setupAOS(action = 'remove') {
		this.querySelectorAll('[data-aos]').forEach((el) => {
			el.classList[action]('aos-animate', 'aos-init')
		})
	}
}

customElements.define('l-drawer', LTADrawer)

/*****************************************
 * [COMPONENT]: L-COLLAPSIBLE-TAB
 ******************************************/
class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.trigger = this.querySelector('[data-trigger]')
		this.content = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = true

		this.transitionendEvent = this.transitionendEventName()

		// Setup default behavior / data
		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.classList.add(this.expandedClass)
		} else {
			this.content.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			l_disableFocusableElements(this.content)
		}

		this.content.removeAttribute('hidden')

		// Events
		this.trigger.addEventListener('click', (e) => this.toggle(e))
		document.addEventListener('keyup', (e) => {
			if (e.code.toUpperCase() !== 'ESCAPE') return
			if (!this.selected || !this.contains(document.activeElement)) return
			this.close()
		})
	}

	toggle(e) {
		e.preventDefault()

		this.selected ? this.close() : this.open()

		/* Close another tab and open only the current tab. */
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

			collapsibleTabs?.length &&
				collapsibleTabs.forEach((tab) => {
					tab !== this && tab.selected && tabsContainer !== tab && tab.close()
				})
		}
	}

	open() {
		this.selected = true
		this.expand()
		this.setAttribute('open', true)

		l_enableFocusableElements(this.content)
		l_trapFocus({ container: this.content, defaultElementToFocus: this.trigger })
	}

	close() {
		this.selected = false
		this.collapse()
		this.removeAttribute('open')

		l_disableFocusableElements(this.content)
		l_removeTrapFocus(this.trigger)
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.content.style.transition = '0'
						this.content.style.height = 'auto'

						requestAnimationFrame(() => {
							this.content.style.height = null
							this.content.style.transition = null
						})
					})
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true
		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)
		this.content.style.height = this.content.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.content) {
				this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.classList.add(this.collapsedClass)
				}
			}
		}).bind(this)

		this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.content.style.transition = '0'
			this.content.style.height = this.content.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.content.style.transition = null
				this.content.style.height = this.collapsedHeight
			})
		})
	}

	destroy() {
		this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
		this.content && this.content.removeAttribute('aria-hidden')
		this.content && (this.content.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/**************************************
 * [COMPONENT]: L-SWIPER-CAROUSEL
 ***************************************/
if (!customElements.get('l-swiper-carousel')) {
	customElements.define(
		'l-swiper-carousel',
		class LTASwiperCarousel extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}

/**************************************
 * [SECTION]: L-COMPARE-PRODUCTS
 ***************************************/
if (!customElements.get('l-compare-products')) {
	class LTACompareProducts extends HTMLElement {
		constructor() {
			super()

			this.onChange = this.onChange.bind(this)
		}

		connectedCallback() {
			this.productCardEls = [...this.querySelectorAll('[data-product]')]
			this.selectEls = [...this.querySelectorAll('[data-product-type]')]
			this.addEventListener('change', this.onChange)
		}

		onChange(event) {
			const { value } = event.target
			const [selectedType, selectedProductId] = value.split('-')

			this.selectEls.forEach((select) => {
				const [productType, productId] = select.dataset.productType.split('-')

				if (productType == selectedType) {
					select.value = value
				}
			})

			this.productCardEls.forEach(({ dataset, classList }) => {
				const [productType, productId] = dataset.product.split('-')

				if (productType == selectedType) {
					classList.toggle('l-hidden', productId !== selectedProductId)
				}
			})
		}
	}

	customElements.define('l-compare-products', LTACompareProducts)
}

/**************************************
 * [SECTION]: L-PRODUCT-CARDS-CAROUSEL
 ***************************************/
if (!customElements.get('l-swiper-product-cards-carousel')) {
	customElements.define(
		'l-swiper-product-cards-carousel',
		class LTAProductCardsSwiperCarousel extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)
				this.activeTextEl = document.getElementById(`CurrentSlide-${this.dataset.sectionId}`)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)

				this.swiperCarousel.on('slideChange', (swiper) => {
					const totalSlides = swiper.slides.length
					let currentSlideIndex = 1
					if (swiper.progress == 1) currentSlideIndex = totalSlides
					else if (swiper.progress == 0) currentSlideIndex = 1
					else
						currentSlideIndex =
							totalSlides * swiper.progress >= totalSlides - 1
								? totalSlides - 1
								: Math.ceil(totalSlides * swiper.progress)

					this.activeTextEl.innerText = currentSlideIndex
				})
			}
		}
	)
}

/*****************************************
 * [COMPONENT]: COUNTDOWN (MEDIA-BANNER)
 ******************************************/
if (!customElements.get('l-countdown')) {
	customElements.define(
		'l-countdown',
		class LTACountDown extends HTMLElement {
			constructor() {
				super()
				this.timer = null
			}

			connectedCallback() {
				// Get countdown elements
				this.dayEl = this.querySelector('[data-days]')
				this.hourEl = this.querySelector('[data-hours]')
				this.minEl = this.querySelector('[data-min]')
				this.secEl = this.querySelector('[data-sec]')

				const raw = this.dataset.anchorTime
				if (!raw) return

				// Always treat as UTC since ISO has 'Z'
				this.end = new Date(raw).getTime()

				// Initial update and start interval
				this.update()
				this.timer = setInterval(() => this.update(), 1000)
			}

			disconnectedCallback() {
				// Clear interval when element is removed
				if (this.timer) clearInterval(this.timer)
			}

			update() {
				const nowUTC = Date.now()
				let diff = this.end - nowUTC
				if (diff < 0) diff = 0

				const days = Math.floor(diff / (1000 * 60 * 60 * 24))
				const hours = Math.floor((diff / (1000 * 60 * 60)) % 24)
				const minutes = Math.floor((diff / (1000 * 60)) % 60)
				const seconds = Math.floor((diff / 1000) % 60)

				// Update elements if they exist
				if (this.dayEl) this.dayEl.textContent = String(days).padStart(2, '0')
				if (this.hourEl) this.hourEl.textContent = String(hours).padStart(2, '0')
				if (this.minEl) this.minEl.textContent = String(minutes).padStart(2, '0')
				if (this.secEl) this.secEl.textContent = String(seconds).padStart(2, '0')

				// Stop interval when countdown reaches zero
				if (diff <= 0 && this.timer) {
					clearInterval(this.timer)
					this.timer = null
				}
			}
		}
	)
}

/**************************************
 * [SECTION]: MINI CART
 ***************************************/
function waitForElement(selector) {
	return new Promise((resolve) => {
		if (document.querySelector(selector)) {
			return resolve(document.querySelector(selector))
		}

		const observer = new MutationObserver(() => {
			if (document.querySelector(selector)) {
				resolve(document.querySelector(selector))
				observer.disconnect()
			}
		})

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		})
	})
}

function decodeHtmlEntities(str) {
	if (!str) return str
	return String(str)
		.replace(/&quot;/g, '"')
		.replace(/&#34;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/&amp;/g, '&')
}

function parseWalletConfigs(raw) {
	if (!raw) return null
	try {
		return JSON.parse(raw)
	} catch (e) {
		try {
			return JSON.parse(decodeHtmlEntities(raw))
		} catch (e2) {
			return null
		}
	}
}

// Force accelerated checkout to show only Shop Pay.
// We replace the element because the wallet grid is rendered inside a closed shadow DOM.
function keepOnlyShopPayInAcceleratedCheckout(el) {
	if (!el || el.dataset?.shopPayOnly === '1') return

	const raw = el.getAttribute('wallet-configs')
	const configs = parseWalletConfigs(raw)
	if (!Array.isArray(configs)) return

	const filtered = configs.filter((c) => c?.name === 'shop_pay')
	if (filtered.length === configs.length && filtered.length === 1) {
		el.dataset.shopPayOnly = '1'
		return
	}

	const replacement = document.createElement('shopify-accelerated-checkout-cart')
	for (const attr of el.getAttributeNames()) {
		if (attr === 'wallet-configs') continue
		replacement.setAttribute(attr, el.getAttribute(attr))
	}
	replacement.setAttribute('wallet-configs', JSON.stringify(filtered))

	Array.from(el.childNodes).forEach((node) => {
		if (
			node.nodeType === 1 &&
			(node.tagName === 'SHOPIFY-GOOGLE-PAY-BUTTON' ||
				node.tagName === 'SHOPIFY-APPLE-PAY-BUTTON' ||
				node.tagName === 'PAYPAL-WALLET-BUTTON' ||
				node.tagName === 'SHOPIFY-PAYPAL-BUTTON' ||
				node.tagName === 'SHOPIFY-AMAZON-PAY-BUTTON')
		)
			return
		replacement.appendChild(node)
	})

	replacement.dataset.shopPayOnly = '1'
	el.replaceWith(replacement)
}

function keepOnlyShopPayInAllAcceleratedCheckout(root = document) {
	root.querySelectorAll('shopify-accelerated-checkout-cart').forEach(keepOnlyShopPayInAcceleratedCheckout)
}

document.addEventListener('DOMContentLoaded', () => {
	keepOnlyShopPayInAllAcceleratedCheckout(document)
	const acceleratedCheckoutObserver = new MutationObserver((mutations) => {
		for (const m of mutations) {
			for (const node of m.addedNodes) {
				if (!(node instanceof Element)) continue
				if (node.matches?.('shopify-accelerated-checkout-cart')) {
					keepOnlyShopPayInAcceleratedCheckout(node)
				} else if (node.querySelector) {
					keepOnlyShopPayInAllAcceleratedCheckout(node)
				}
			}
		}
	})

	acceleratedCheckoutObserver.observe(document.body, { childList: true, subtree: true })

	waitForElement('#dynamic-checkout-cart').then((additionalCheckoutButtonsEl) => {
		const initializeCheckoutButtons = () => {
			const checkoutGroup = document.getElementById('dynamic-checkout-cart')
			const googlePayButton = checkoutGroup?.querySelector('shopify-google-pay-button')
			const shopPayButton = checkoutGroup?.querySelector('shop-pay-wallet-button')
			const applePayButton = checkoutGroup?.querySelector('shopify-apple-pay-button')

			// If no buttons found, retry after 1s
			if (!googlePayButton && !shopPayButton && !applePayButton) {
				setTimeout(initializeCheckoutButtons, 1000)

				return
			}

			function trackQuickCheckoutEvent(tagName) {
				window.igEvents = window.igEvents || []
				window.igEvents.push({ event: `Quick Checkout - ${tagName}` })
			}

			checkoutGroup.addEventListener('click', (event) => {
				const target = event.target

				if (googlePayButton?.contains(target)) {
					trackQuickCheckoutEvent('Google Pay')
				}

				if (shopPayButton?.contains(target)) {
					trackQuickCheckoutEvent('Shop Pay')
				}

				if (applePayButton?.contains(target)) {
					trackQuickCheckoutEvent('Apple Pay')
				}
			})
		}

		// Initial delay of 2s before checking for buttons
		setTimeout(initializeCheckoutButtons, 2000)
	})
})

